import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Loader2,
  Ticket,
  TrendingUp,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ServiceDeskAPI, ptsOpenProjectListQueryKey } from "@sd/services/APIs";
import { projectFilterOptionsWithSelection } from "@sd/lib/ptsDropdownOptions";
import { SearchableMultiFilterSelect } from "@sd/components/common/SearchableMultiFilterSelect";
import { useServiceDeskUserDisplayMap } from "@sd/hooks/useServiceDeskUserDisplayMap";
import { sd } from "@sd/lib/sdPaths";
import { buildAdminAgentInProgressTicketsPath } from "@sd/lib/adminTicketUtils";
import { cn } from "@/lib/utils";

type SortField = "name" | "open" | "assigned";
type SortDir = "asc" | "desc";

interface AgentRow {
  id: string;
  empId: string;
  name: string;
  open: number;
  assigned: number;
}

function extractAgents(raw: unknown): AgentRow[] {
  const d = (raw ?? {}) as Record<string, unknown>;
  const arr: Record<string, unknown>[] = (() => {
    for (const k of ["agent_status", "agents", "agent_list"]) {
      const v = d[k];
      if (Array.isArray(v)) return v as Record<string, unknown>[];
    }
    return [];
  })();

  return arr.map((a, i) => {
    const name =
      String(a.employee_name ?? a.name ?? a.full_name ?? `Agent ${i + 1}`);
    const open =
      Number(
        a.all_in_progress_tickets_count ??
          a.all_pending_open_tickets_count ??
          a.open_tickets ??
          a.pending_tickets ??
          0
      ) || 0;
    const assigned =
      Number(a.all_assigned_tickets_count ?? a.assigned_tickets ?? 0) || 0;
    const empId = String(a.emp_id ?? a.id ?? i);
    return { id: String(a.id ?? i), empId, name, open, assigned };
  });
}

function openBadgeColor(open: number): string {
  if (open === 0) return "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";
  if (open <= 5) return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
  if (open <= 15) return "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400";
  return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
}

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
  "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
];

function avatarColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) {
    h = (h * 31 + name.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() ?? "")
    .join("");
}

export function AdminAgentsPendingPage() {
  const navigate = useNavigate();
  const [sortField, setSortField] = useState<SortField>("open");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [projectFilters, setProjectFilters] = useState<string[]>([]);
  const [assigneeFilters, setAssigneeFilters] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 100;

  const { data: ptsProjectsData, isPending: projectsLoading } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => (await ServiceDeskAPI.pts.openProjectList()).data,
    staleTime: 5 * 60_000,
  });

  const projectOptions = useMemo(
    () => projectFilterOptionsWithSelection(ptsProjectsData, projectFilters),
    [ptsProjectsData, projectFilters]
  );

  const { agentEmployees } = useServiceDeskUserDisplayMap({
    refetchAgentListsOnMount: true,
  });

  const assigneeOptions = useMemo(
    () => agentEmployees.map(a => ({ value: a.id, label: a.name })),
    [agentEmployees]
  );

  // Server-side pagination: send only `page` + `page_size` by default.
  // `project` is sent only when the user explicitly picks one in the filter.
  const {
    data: rawAgentStatus,
    isPending: agentsPending,
    isFetching: agentsFetching,
    isError,
  } = useQuery({
    queryKey: ["sd-agent-status", page, PAGE_SIZE, projectFilters, assigneeFilters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        page,
        page_size: PAGE_SIZE,
      };
      const projects = projectFilters.filter(Boolean);
      if (projects.length > 0) params.project = projects;
      const assignees = assigneeFilters.filter(Boolean);
      if (assignees.length > 0) params.assigned_to = assignees;
      return (await ServiceDeskAPI.dashboard.agentStatus(params)).data;
    },
    staleTime: 60_000,
    retry: 1,
  });

  const isPending = agentsPending;

  const allAgents = useMemo(
    () => extractAgents(rawAgentStatus),
    [rawAgentStatus]
  );

  const totalCount = useMemo(() => {
    const c = Number((rawAgentStatus as Record<string, unknown>)?.count);
    return Number.isFinite(c) && c >= 0 ? c : allAgents.length;
  }, [rawAgentStatus, allAgents.length]);

  const summary = useMemo(
    () => ({
      inProgress: allAgents.reduce((sum, a) => sum + a.open, 0),
      assigned: allAgents.reduce((sum, a) => sum + a.assigned, 0),
    }),
    [allAgents]
  );

  // Sort the current page in place (server returns one page at a time).
  const sorted = useMemo(() => {
    return [...allAgents].sort((a, b) => {
      let cmp = 0;
      if (sortField === "name") cmp = a.name.localeCompare(b.name);
      else if (sortField === "open") cmp = a.open - b.open;
      else cmp = a.assigned - b.assigned;
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [allAgents, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedAgents = sorted;

  // Reset to first page when the filters change.
  useEffect(() => {
    setPage(1);
  }, [projectFilters, assigneeFilters]);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const goToAgentTickets = (agent: AgentRow) => {
    navigate(
      buildAdminAgentInProgressTicketsPath(agent.empId, {
        projectFilters,
      })
    );
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <ArrowUpDown
      className={cn(
        "ml-1 inline h-3.5 w-3.5",
        sortField === field ? "text-primary" : "text-muted-foreground/40"
      )}
    />
  );

  return (
    <div className="min-h-0 space-y-5 p-4 sm:p-6">
      {/* Back */}
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 h-8 gap-1.5 text-muted-foreground hover:text-foreground"
        onClick={() => navigate(sd("admin"))}
      >
        <ArrowLeft className="h-4 w-4" />
        Dashboard
      </Button>

      {/* Hero header */}
      <div className="overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card shadow-sm">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-sm">
              <Users className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                Agents — Most In Progress
              </h1>
              <p className="text-sm text-muted-foreground">
                Workload across all agents, sorted by in progress tickets
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="w-full sm:w-48">
              <SearchableMultiFilterSelect
                selectId="agents-pending-assignee"
                values={assigneeFilters}
                onValuesChange={setAssigneeFilters}
                options={assigneeOptions}
                placeholder="All assignees"
                searchPlaceholder="Search assignees…"
                disabled={assigneeOptions.length === 0}
              />
            </div>
            <div className="w-full sm:w-48">
              <SearchableMultiFilterSelect
                selectId="agents-pending-project"
                values={projectFilters}
                onValuesChange={setProjectFilters}
                options={projectOptions}
                placeholder="All projects"
                searchPlaceholder="Search projects…"
                disabled={projectsLoading}
              />
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border bg-card/60">
          {[
            { label: "Agents", value: totalCount, Icon: Users, tint: "text-primary" },
            { label: "In Progress", value: summary.inProgress, Icon: TrendingUp, tint: "text-orange-600 dark:text-orange-400" },
            { label: "Assigned", value: summary.assigned, Icon: ClipboardList, tint: "text-blue-600 dark:text-blue-400" },
          ].map(({ label, value, Icon, tint }) => (
            <div key={label} className="flex items-center gap-3 px-4 py-3 sm:px-5">
              <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted", tint)}>
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-lg font-bold leading-none tabular-nums text-foreground">
                  {agentsFetching ? "…" : value}
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      {isPending ? (
        <div className="space-y-2 rounded-xl border border-border bg-card p-3 shadow-sm">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 rounded-lg px-2 py-2.5">
              <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-4 w-40 animate-pulse rounded bg-muted" />
              <div className="ml-auto h-6 w-12 animate-pulse rounded-full bg-muted" />
              <div className="h-4 w-8 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          Failed to load agent data. Please try again.
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex h-48 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card text-center">
          <Users className="h-8 w-8 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            No agent data available.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full table-fixed border-collapse text-sm">
            <colgroup>
              <col className="w-[52%]" />
              <col className="w-[24%]" />
              <col className="w-[24%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left sm:px-5">
                  <button
                    type="button"
                    className="flex items-center text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-primary"
                    onClick={() => toggleSort("name")}
                  >
                    Agent <SortIcon field="name" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    type="button"
                    className="mx-auto flex items-center justify-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-primary"
                    onClick={() => toggleSort("open")}
                  >
                    In Progress <SortIcon field="open" />
                  </button>
                </th>
                <th className="px-4 py-3 text-center">
                  <button
                    type="button"
                    className="mx-auto flex items-center justify-center gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-primary"
                    onClick={() => toggleSort("assigned")}
                  >
                    Assigned <SortIcon field="assigned" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedAgents.map((agent, idx) => {
                const rank = (currentPage - 1) * PAGE_SIZE + idx + 1;
                return (
                  <tr
                    key={agent.id}
                    className="group cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-primary/5"
                    onClick={() => goToAgentTickets(agent)}
                    title={`View tickets for ${agent.name}`}
                  >
                    <td className="px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-3">
                        <span className="w-5 shrink-0 text-right text-xs font-medium tabular-nums text-muted-foreground/60">
                          {rank}
                        </span>
                        <div
                          className={cn(
                            "flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                            avatarColor(agent.name)
                          )}
                        >
                          {initials(agent.name) || "?"}
                        </div>
                        <span className="truncate font-medium text-foreground group-hover:text-primary">
                          {agent.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Badge
                        className={cn(
                          "gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                          openBadgeColor(agent.open)
                        )}
                      >
                        <Ticket className="h-3 w-3" />
                        {agent.open}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="font-semibold tabular-nums text-foreground">
                          {agent.assigned}
                        </span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-muted/30 px-4 py-2.5 text-xs text-muted-foreground sm:px-5">
            <span>
              Showing{" "}
              <span className="font-medium text-foreground">
                {(currentPage - 1) * PAGE_SIZE + 1}–
                {(currentPage - 1) * PAGE_SIZE + pagedAgents.length}
              </span>{" "}
              of <span className="font-medium text-foreground">{totalCount}</span> agents
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 px-2"
                disabled={currentPage <= 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev
              </Button>
              <span className="tabular-nums">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1 px-2"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
